// @ts-ignore
import Lock from 'browser-tabs-lock';

import {
    parseAuthenticationResult,
    validateCrypto,
    getDomain,
    getTokenIssuer,
    parseNumber, createQueryParams, patchOpenUrlWithOnRedirect
} from './utils';

import {oauthToken} from './api';

import {getUniqueScopes} from './scope';

import {
    InMemoryCache,
    ICache,
    CacheKey,
    CacheManager,
    CacheEntry,
    IdTokenEntry,
    CACHE_KEY_ID_TOKEN_SUFFIX,
    DecodedToken
} from './cache';

import {verify as verifyIdToken} from './jwt';
import {
    AuthenticationError,
    TimeoutError
} from './errors';

import {
    ClientStorage,
    CookieStorage,
    CookieStorageWithLegacySameSite,
} from './storage';

import {
    CACHE_LOCATION_MEMORY,
    DEFAULT_SCOPE,
    DEFAULT_SESSION_CHECK_EXPIRY_DAYS,
    DEFAULT_NOW_PROVIDER,
    DEFAULT_FETCH_TIMEOUT_MS, DEFAULT_AUTH0_CLIENT
} from './constants';

import {
    Auth0ClientOptions,
    AuthorizationParams,
    RedirectLoginResult,
    GetTokenSilentlyOptions,
    CacheLocation,
    User,
    GetTokenSilentlyVerboseResponse,
    TokenEndpointResponse
} from './global';

import {singlePromise, retryPromise} from './promise-utils';
import {CacheKeyManifest} from './cache/key-manifest';
import {
    buildIsAuthenticatedCookieName,
    cacheFactory,
    GET_TOKEN_SILENTLY_LOCK_KEY,
    OLD_IS_AUTHENTICATED_COOKIE_NAME,
} from './Auth0Client.utils';
import {LogoutUrlOptions} from "@auth0/auth0-react";
import {LogoutOptions} from "@auth0/auth0-react/src/auth0-context.tsx";

/**
 * @ignore
 */
type GetTokenSilentlyResult = TokenEndpointResponse & {
    decodedToken: ReturnType<typeof verifyIdToken>;
    scope: string;
    oauthTokenScope?: string;
    audience: string;
};

/**
 * @ignore
 */
const lock = new Lock();

/**
 * Auth0 SDK for Single Page Applications using [Authorization Code Grant Flow with PKCE](https://auth0.com/docs/api-auth/tutorials/authorization-code-grant-pkce).
 */
export class Auth0Client {
    private readonly cacheManager: CacheManager;
    private readonly domainUrl: string;
    private readonly tokenIssuer: string;
    private readonly scope: string;
    private readonly cookieStorage: ClientStorage;
    private readonly sessionCheckExpiryDays: number;
    private readonly isAuthenticatedCookieName: string;
    private readonly nowProvider: () => number | Promise<number>;
    private readonly httpTimeoutMs: number;
    private readonly options: Auth0ClientOptions & {
        authorizationParams: AuthorizationParams;
    };
    private readonly userCache: ICache = new InMemoryCache().enclosedCache;


    private readonly defaultOptions: Partial<Auth0ClientOptions> = {
        authorizationParams: {
            scope: DEFAULT_SCOPE
        },
        useRefreshTokensFallback: false,
        useFormData: true
    };

    constructor(options: Auth0ClientOptions) {
        this.options = {
            ...this.defaultOptions,
            ...options,
            authorizationParams: {
                ...this.defaultOptions.authorizationParams,
                ...options.authorizationParams
            }
        };

        typeof window !== 'undefined' && validateCrypto();

        if (options.cache && options.cacheLocation) {
            console.warn(
                'Both `cache` and `cacheLocation` options have been specified in the Auth0Client configuration; ignoring `cacheLocation` and using `cache`.'
            );
        }

        let cacheLocation: CacheLocation | undefined;
        let cache: ICache;

        if (options.cache) {
            cache = options.cache;
        } else {
            cacheLocation = options.cacheLocation || CACHE_LOCATION_MEMORY;

            if (!cacheFactory(cacheLocation)) {
                throw new Error(`Invalid cache location "${cacheLocation}"`);
            }

            cache = cacheFactory(cacheLocation)();
        }

        this.httpTimeoutMs = options.httpTimeoutInSeconds
            ? options.httpTimeoutInSeconds * 1000
            : DEFAULT_FETCH_TIMEOUT_MS;

        this.cookieStorage =
            options.legacySameSiteCookie === false
                ? CookieStorage
                : CookieStorageWithLegacySameSite;


        this.isAuthenticatedCookieName = buildIsAuthenticatedCookieName(
            this.options.clientId
        );

        this.sessionCheckExpiryDays =
            options.sessionCheckExpiryDays || DEFAULT_SESSION_CHECK_EXPIRY_DAYS;

        // Construct the scopes based on the following:
        // 1. Always include `openid`
        // 2. Include the scopes provided in `authorizationParams. This defaults to `profile email`
        // 3. Add `offline_access` if `useRefreshTokens` is enabled
        this.scope = getUniqueScopes(
            'openid',
            this.options.authorizationParams.scope,
            this.options.useRefreshTokens ? 'offline_access' : ''
        );
        this.nowProvider = this.options.nowProvider || DEFAULT_NOW_PROVIDER;

        this.cacheManager = new CacheManager(
            cache,
            !cache.allKeys
                ? new CacheKeyManifest(cache, this.options.clientId)
                : undefined,
            this.nowProvider
        );

        this.domainUrl = getDomain(this.options.domain);
        this.tokenIssuer = getTokenIssuer(this.options.issuer, this.domainUrl);

    }

    private _url(path: string) {
        const auth0Client = encodeURIComponent(
            btoa(JSON.stringify(this.options.auth0Client || DEFAULT_AUTH0_CLIENT))
        );
        return `${this.domainUrl}${path}&auth0Client=${auth0Client}`;
    }


    private _buildLogoutUrl(options: LogoutUrlOptions): string {
        if (options.clientId !== null) {
            options.clientId = options.clientId || this.options.clientId;
        } else {
            delete options.clientId;
        }

        const {federated, ...logoutOptions} = options.logoutParams || {};
        const federatedQuery = federated ? `&federated` : '';
        const url = this._url(
            `/v2/logout?${createQueryParams({
                clientId: options.clientId,
                ...logoutOptions
            })}`
        );

        return url + federatedQuery;
    }

    private async _verifyIdToken(
        id_token: string,
        nonce?: string,
        organization?: string
    ) {
        const now = await this.nowProvider();

        return verifyIdToken({
            iss: this.tokenIssuer,
            aud: this.options.clientId,
            id_token,
            nonce,
            organization,
            leeway: this.options.leeway,
            max_age: parseNumber(this.options.authorizationParams.max_age),
            now
        });
    }


    /**
     * ```js
     * const user = await auth0.getUser();
     * ```
     *
     * Returns the user information if available (decoded
     * from the `id_token`).
     *
     * @typeparam TUser The type to return, has to extend {@link User}.
     */
    public async getUser<TUser extends User>(): Promise<TUser | undefined> {
        const cache = await this._getIdTokenFromCache();

        return cache?.decodedToken?.user as TUser;
    }


    /**
     * After the browser redirects back to the callback page,
     * call `handleRedirectCallback` to handle success and error
     * responses from Auth0. If the response is successful, results
     * will be valid according to their expiration times.
     */
    public async handleRedirectCallback<TAppState = any>(
        url: string = window.location.href
    ): Promise<RedirectLoginResult<TAppState>> {
        const queryStringFragments = url.split('?').slice(1);

        if (queryStringFragments.length === 0) {
            throw new Error('There are no query params available for parsing.');
        }

        const {state, code, verifier, redirect_uri, error, error_description} = parseAuthenticationResult(
            queryStringFragments.join('')
        );
        this.options.authorizationParams.redirect_uri = redirect_uri

        if (error) {
            throw new AuthenticationError(
                error,
                error_description || error,
                state,
            );
        }


        await this._requestToken(
            {
                audience: this.options.authorizationParams.audience,
                scope: this.scope,
                code_verifier: verifier,
                grant_type: 'authorization_code',
                code: code as string,
                redirect_uri: redirect_uri
            },
            {}
        );
        return {};
    }

    /**
     * ```js
     * await auth0.checkSession();
     * ```
     *
     * Check if the user is logged in using `getTokenSilently`. The difference
     * with `getTokenSilently` is that this doesn't return a token, but it will
     * pre-fill the token cache.
     *
     * This method also heeds the `auth0.{clientId}.is.authenticated` cookie, as an optimization
     *  to prevent calling Auth0 unnecessarily. If the cookie is not present because
     * there was no previous login (or it has expired) then tokens will not be refreshed.
     *
     * It should be used for silently logging in the user when you instantiate the
     * `Auth0Client` constructor. You should not need this if you are using the
     * `createAuth0Client` factory.
     *
     * **Note:** the cookie **may not** be present if running an app using a private tab, as some
     * browsers clear JS cookie data and local storage when the tab or page is closed, or on page reload. This effectively
     * means that `checkSession` could silently return without authenticating the user on page refresh when
     * using a private tab, despite having previously logged in. As a workaround, use `getTokenSilently` instead
     * and handle the possible `login_required` error [as shown in the readme](https://github.com/auth0/auth0-spa-js#creating-the-client).
     *
     * @param options
     */
    public async checkSession(options?: GetTokenSilentlyOptions) {
        if (!this.cookieStorage.get(this.isAuthenticatedCookieName)) {
            if (!this.cookieStorage.get(OLD_IS_AUTHENTICATED_COOKIE_NAME)) {
                return;
            } else {
                // Migrate the existing cookie to the new name scoped by client ID
                this.cookieStorage.save(this.isAuthenticatedCookieName, true, {
                    daysUntilExpire: this.sessionCheckExpiryDays,
                    cookieDomain: this.options.cookieDomain
                });

                this.cookieStorage.remove(OLD_IS_AUTHENTICATED_COOKIE_NAME);
            }
        }

        try {
            await this.getTokenSilently(options);
        } catch (_) {
        }
    }

    /**
     * Fetches a new access token, and either returns just the access token (the default) or the response from the /oauth/token endpoint, depending on the `detailedResponse` option.
     *
     * ```js
     * const token = await auth0.getTokenSilently(options);
     * ```
     *
     * If there's a valid token stored and it has more than 60 seconds
     * remaining before expiration, return the token. Otherwise, attempt
     * to obtain a new token.
     *
     * A new token will be obtained either by opening an iframe or a
     * refresh token (if `useRefreshTokens` is `true`).

     * If iframes are used, opens an iframe with the `/authorize` URL
     * using the parameters provided as arguments. Random and secure `state`
     * and `nonce` parameters will be auto-generated. If the response is successful,
     * results will be validated according to their expiration times.
     *
     * If refresh tokens are used, the token endpoint is called directly with the
     * 'refresh_token' grant. If no refresh token is available to make this call,
     * the SDK will only fall back to using an iframe to the '/authorize' URL if
     * the `useRefreshTokensFallback` setting has been set to `true`. By default this
     * setting is `false`.
     *
     * This method may use a web worker to perform the token call if the in-memory
     * cache is used.
     *
     * If an `audience` value is given to this function, the SDK always falls
     * back to using an iframe to make the token exchange.
     *
     * Note that in all cases, falling back to an iframe requires access to
     * the `auth0` cookie.
     *
     * @param options
     */
    public async getTokenSilently(
        options: GetTokenSilentlyOptions = {}
    ): Promise<undefined | string | GetTokenSilentlyVerboseResponse> {
        const localOptions: GetTokenSilentlyOptions & {
            authorizationParams: AuthorizationParams & { scope: string };
        } = {
            cacheMode: 'on',
            ...options,
            authorizationParams: {
                ...this.options.authorizationParams,
                ...options.authorizationParams,
                scope: getUniqueScopes(this.scope, options.authorizationParams?.scope)
            }
        };

        const result = await singlePromise(
            () => this._getTokenSilently(localOptions),
            `${this.options.clientId}::${localOptions.authorizationParams.audience}::${localOptions.authorizationParams.scope}`
        );

        return options.detailedResponse ? result : result?.access_token;
    }

    private async _getTokenSilently(
        options: GetTokenSilentlyOptions & {
            authorizationParams: AuthorizationParams & { scope: string };
        }
    ): Promise<undefined | GetTokenSilentlyVerboseResponse> {
        const {cacheMode, ...getTokenOptions} = options;

        // Check the cache before acquiring the lock to avoid the latency of
        // `lock.acquireLock` when the cache is populated.
        if (cacheMode !== 'off') {
            const entry = await this._getEntryFromCache({
                scope: getTokenOptions.authorizationParams.scope,
                audience: getTokenOptions.authorizationParams.audience || 'default',
                clientId: this.options.clientId
            });

            if (entry) {
                return entry;
            }
        }

        if (cacheMode === 'cache-only') {
            return;
        }

        if (
            await retryPromise(
                () => lock.acquireLock(GET_TOKEN_SILENTLY_LOCK_KEY, 5000),
                10
            )
        ) {
            try {
                window.addEventListener('pagehide', this._releaseLockOnPageHide);

                // Check the cache a second time, because it may have been populated
                // by a previous call while this call was waiting to acquire the lock.
                if (cacheMode !== 'off') {
                    const entry = await this._getEntryFromCache({
                        scope: getTokenOptions.authorizationParams.scope,
                        audience: getTokenOptions.authorizationParams.audience || 'default',
                        clientId: this.options.clientId
                    });

                    if (entry) {
                        return entry;
                    }
                }

                const authResult = await this._getTokenUsingRefreshToken(getTokenOptions)

                const {id_token, access_token, oauthTokenScope, expires_in} =
                    authResult;

                return {
                    id_token,
                    access_token,
                    ...(oauthTokenScope ? {scope: oauthTokenScope} : null),
                    expires_in
                };
            } finally {
                await lock.releaseLock(GET_TOKEN_SILENTLY_LOCK_KEY);
                window.removeEventListener('pagehide', this._releaseLockOnPageHide);
            }
        } else {
            throw new TimeoutError();
        }
    }


    private async _getTokenUsingRefreshToken(
        options: GetTokenSilentlyOptions & {
            authorizationParams: AuthorizationParams & { scope: string };
        }
    ): Promise<GetTokenSilentlyResult> {
        const cache = await this.cacheManager.get(
            new CacheKey({
                scope: options.authorizationParams.scope,
                audience: options.authorizationParams.audience || 'default',
                clientId: this.options.clientId
            })
        );


        const redirect_uri =
            options.authorizationParams.redirect_uri ||
            this.options.authorizationParams.redirect_uri ||
            window.location.origin;

        const timeout =
            typeof options.timeoutInSeconds === 'number'
                ? options.timeoutInSeconds * 1000
                : null;

        try {
            const tokenResult = await this._requestToken({
                ...options.authorizationParams,
                grant_type: 'refresh_token',
                refresh_token: cache && cache.refresh_token,
                redirect_uri,
                ...(timeout && {timeout})
            });

            return {
                ...tokenResult,
                scope: options.authorizationParams.scope,
                oauthTokenScope: tokenResult.scope,
                audience: options.authorizationParams.audience || 'default'
            };
        } catch (e) {
            throw e;
        }
    }

    private async _saveEntryInCache(
        entry: CacheEntry & { id_token: string; decodedToken: DecodedToken }
    ) {
        const {id_token, decodedToken, ...entryWithoutIdToken} = entry;

        this.userCache.set(CACHE_KEY_ID_TOKEN_SUFFIX, {
            id_token,
            decodedToken
        });

        await this.cacheManager.setIdToken(
            this.options.clientId,
            entry.id_token,
            entry.decodedToken
        );

        await this.cacheManager.set(entryWithoutIdToken);
    }

    private async _getIdTokenFromCache() {
        const audience = this.options.authorizationParams.audience || 'default';

        const cache = await this.cacheManager.getIdToken(
            new CacheKey({
                clientId: this.options.clientId,
                audience,
                scope: this.scope
            })
        );

        const currentCache = this.userCache.get<IdTokenEntry>(
            CACHE_KEY_ID_TOKEN_SUFFIX
        ) as IdTokenEntry;

        // If the id_token in the cache matches the value we previously cached in memory return the in-memory
        // value so that object comparison will work
        if (cache && cache.id_token === currentCache?.id_token) {
            return currentCache;
        }

        this.userCache.set(CACHE_KEY_ID_TOKEN_SUFFIX, cache);
        return cache;
    }

    private async _getEntryFromCache({
                                         scope,
                                         audience,
                                         clientId
                                     }: {
        scope: string;
        audience: string;
        clientId: string;
    }): Promise<undefined | GetTokenSilentlyVerboseResponse> {
        const entry = await this.cacheManager.get(
            new CacheKey({
                scope,
                audience,
                clientId
            }),
            60 // get a new token if within 60 seconds of expiring
        );

        if (entry && entry.access_token) {
            const {access_token, oauthTokenScope, expires_in} = entry as CacheEntry;
            const cache = await this._getIdTokenFromCache();
            return (
                cache && {
                    id_token: cache.id_token,
                    access_token,
                    ...(oauthTokenScope ? {scope: oauthTokenScope} : null),
                    expires_in
                }
            );
        }
    }

    /**
     * Releases any lock acquired by the current page that's not released yet
     *
     * Get's called on the `pagehide` event.
     * https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
     */
    private _releaseLockOnPageHide = async () => {
        await lock.releaseLock(GET_TOKEN_SILENTLY_LOCK_KEY);

        window.removeEventListener('pagehide', this._releaseLockOnPageHide);
    };

    private async _requestToken(
        options: PKCERequestTokenOptions | RefreshTokenRequestTokenOptions,
        additionalParameters?: RequestTokenAdditionalParameters
    ) {
        const {nonceIn, organization} = additionalParameters || {};
        const authResult = await oauthToken(
            {
                baseUrl: this.domainUrl,
                client_id: this.options.clientId,
                auth0Client: this.options.auth0Client,
                useFormData: this.options.useFormData,
                timeout: this.httpTimeoutMs,
                ...options
            }
        );

        const decodedToken = await this._verifyIdToken(
            authResult.id_token,
            nonceIn,
            organization
        );

        await this._saveEntryInCache({
            ...authResult,
            decodedToken,
            scope: options.scope,
            audience: options.audience || 'default',
            ...(authResult.scope ? {oauthTokenScope: authResult.scope} : null),
            client_id: this.options.clientId
        });

        this.cookieStorage.save(this.isAuthenticatedCookieName, true, {
            daysUntilExpire: this.sessionCheckExpiryDays,
            cookieDomain: this.options.cookieDomain
        });

        return {...authResult, decodedToken};
    }

    public async logout(options: LogoutOptions = {}): Promise<void> {
        const {openUrl, ...logoutOptions} = patchOpenUrlWithOnRedirect(options);

        if (options.clientId === null) {
            await this.cacheManager.clear();
        } else {
            await this.cacheManager.clear(options.clientId || this.options.clientId);
        }

        this.cookieStorage.remove(this.isAuthenticatedCookieName, {
            cookieDomain: this.options.cookieDomain
        });
        this.userCache.remove(CACHE_KEY_ID_TOKEN_SUFFIX);

        const url = this._buildLogoutUrl(logoutOptions);

        if (openUrl) {
            await openUrl(url);
        } else if (openUrl !== false) {
            window.location.assign(url);
        }
    }
}

interface BaseRequestTokenOptions {
    audience?: string;
    scope: string;
    timeout?: number;
    redirect_uri?: string;
}

interface PKCERequestTokenOptions extends BaseRequestTokenOptions {
    code: string;
    grant_type: 'authorization_code';
    code_verifier: string;
}

interface RefreshTokenRequestTokenOptions extends BaseRequestTokenOptions {
    grant_type: 'refresh_token';
    refresh_token?: string;
}

interface RequestTokenAdditionalParameters {
    nonceIn?: string;
    organization?: string;
}
